import React, { useEffect, useState, useRef, Children } from 'react';
import { useInView } from 'react-intersection-observer';

import { Draggable } from 'gsap/Draggable';
import { useResize } from 'hooks/use-resize';

import Arrow from 'assets/svg/small-arrow.svg';

import s from './Gallery.scss';
import gsap from 'gsap';

interface IGalleryProps {
  children: React.ReactNode;
  display?: string;
  isColumnLayout?: boolean;
}

// if sidebar
const PAGE_LIMIT = 1620;

export const Gallery = ({ children, display, isColumnLayout = false }: IGalleryProps) => {
  if (!children) {
    return null;
  }

  const { sizes } = useResize();

  const [galleryWidth, setGalleryWidth] = useState(0);
  const [isDraggable, setDraggable] = useState(true);
  const [scrollStart, setScrollStart] = useState(false);
  const [scrollEnd, setScrollEnd] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeItem, setActiveItem] = useState(1);
  const [startRef, startInView] = useInView();
  const [endRef, endInView] = useInView();

  const galleryRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const setWidth = () => {
    if (
      !innerRef.current ||
      !rowRef.current ||
      !containerRef.current ||
      typeof window === undefined ||
      display !== 'Horizontal'
    ) {
      return;
    }

    const windowWidth = window.innerWidth;
    const rowWidth = rowRef.current.scrollWidth;
    const rowX = rowRef.current.getBoundingClientRect().left;

    setDraggable(windowWidth < rowWidth + rowX);

    const containerStyles = window.getComputedStyle(containerRef.current);
    const containerPadding = parseInt(containerStyles.getPropertyValue('padding-left'), 10);

    if (windowWidth > PAGE_LIMIT) {
      const offset = windowWidth - containerRef.current.getBoundingClientRect().width;
      setGalleryWidth(rowWidth + offset + containerPadding);
    } else {
      setGalleryWidth(rowWidth + containerPadding * 2);
    }
  };

  useEffect(() => {
    setWidth();
  }, [sizes]);

  useEffect(() => {
    setWidth();

    if (display === 'Horizontal' && isDraggable) {
      const drag = Draggable.create(innerRef.current, {
        type: 'x',
        inertia: true,
        bounds: `.${s.gallery}`,
        zIndexBoost: false,
      });

      return () => {
        if (drag) {
          drag[0].kill();
        }
      };
    } else {
      gsap.set(innerRef.current, { x: 0 });
    }
  }, [isDraggable]);

  //  at start of carousel
  useEffect(() => {
    if (display !== 'Horizontal') {
      return;
    }

    setScrollStart(startInView);
  }, [startInView]);

  //  at end of carousel
  useEffect(() => {
    if (display !== 'Horizontal') {
      return;
    }

    setScrollEnd(endInView);
  }, [endInView]);

  useEffect(() => {
    if (!innerRef.current || !containerRef.current || display !== 'Horizontal') {
      return;
    }

    innerRef.current.style.width = `${galleryWidth}px`;
  }, [galleryWidth]);

  const move = (direction: 'forwards' | 'backwards') => {
    const currentItem = galleryRef.current?.querySelector(`.${s.gallery__item}:nth-child(${activeItem})`);
    const childCount = Children.count(children);

    if (!galleryRef.current || !currentItem || !childCount || isAnimating) {
      return;
    }
    
    // activeitem is current index
    if (activeItem > childCount) {
      setActiveItem(childCount);

      return;
    }

    setIsAnimating(true);

    const onComplete = () => {
      if (activeItem > childCount) {
        setActiveItem(childCount); 
      }
      if (direction === 'forwards' && activeItem < childCount) {
        setActiveItem(activeItem + 1);
      } else if (direction === 'backwards' && activeItem > 2) {
        setActiveItem(activeItem - 1);
      }

      setIsAnimating(false);
    }

    const tl = gsap.timeline({ onComplete });
    const itemDimensions = gsap.getProperty(currentItem);
    const width = itemDimensions('width') as number;
    const margin = itemDimensions('marginLeft') as number;
    const currentX = gsap.getProperty(innerRef.current, 'x') as number;

    const x = direction === 'forwards' ?
    -(width + (-currentX) + margin) :
    -(-width + (-currentX) + margin);

    tl.to(innerRef.current, {
      x,
      duration: 0.3,
    });
  }

  return (
    <div
      ref={galleryRef}
      className={s('gallery', display?.toLowerCase(), {
        isDraggable: isDraggable && display === 'Horizontal',
        isColumnLayout,
      })}
    >
      {display === 'Horizontal' && (
        <div className={s('gallery__controls', { active: startInView || endInView })}>
          <button disabled={scrollStart} className={s('gallery__button', 'prev')} onClick={() => move('backwards')}>
            <Arrow />
          </button>
          <button disabled={scrollEnd} className={s.gallery__button} onClick={() => move('forwards')}>
            <Arrow />
          </button>
        </div>
      )}
      <div className={s.gallery__inner} ref={innerRef}>
        <div style={{ height: 1, width: 1 }} ref={startRef} />

        <div className={s.gallery__containerWrap}>
          <div
            className={s.gallery__container}
            ref={containerRef}
          >
            <div className={s.gallery__row} ref={rowRef}>
              {children}
              <div style={{ height: 1, width: 1, flex: 'none' }} ref={endRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
